import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FileDown, Award, AlertTriangle } from "lucide-react";
import { getCertificate, certificatePdfUrl } from "@/api";

export default function CertificateView() {
  const { token } = useParams();
  const [cert, setCert] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getCertificate(token)
      .then(setCert)
      .catch(() => setError("This certificate link is invalid. Call 010 534 9926 if you need help."));
  }, [token]);

  if (error) {
    return (
      <div data-testid="certificate-error" className="min-h-screen flex items-center justify-center px-4 pt-20 text-center">
        <div>
          <AlertTriangle size={48} className="text-amber-400 mx-auto mb-6" />
          <h1 className="font-display text-3xl font-black text-white mb-3">Certificate not found</h1>
          <p className="text-slate-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!cert) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="certificate-loading">Loading certificate…</div>;
  }

  return (
    <div data-testid="certificate-page" className="min-h-screen flex items-center justify-center px-4 pt-24 pb-16 relative overflow-hidden">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-2xl border-2 border-[#F57C00]/50 rounded-2xl bg-[#122659] p-10 md:p-14 text-center relative"
      >
        <div className="absolute inset-2 border border-sky-300/30 rounded-xl pointer-events-none" />
        <Award size={52} className="text-[#F57C00] mx-auto mb-6" />
        <p className="text-xs uppercase tracking-[0.35em] text-sky-300 font-bold mb-4">CompCar Motors</p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-black text-white mb-2">
          Certificate of Ownership
        </h1>
        <p className="text-[#F57C00] text-sm font-semibold mb-10">Every drive builds ownership.</p>

        <p className="text-slate-400 text-sm mb-2">This certifies that</p>
        <p className="font-display text-3xl font-black text-white mb-4" data-testid="certificate-name">{cert.full_name}</p>
        <p className="text-slate-400 text-sm mb-2">has completed their rent-to-own journey and owns</p>
        <p className="font-display text-2xl font-black text-[#F57C00] mb-8" data-testid="certificate-vehicle">{cert.vehicle}</p>
        <p className="text-xs text-slate-500 mb-10">
          Reference {cert.reference}
          {cert.issued_at && <> · Issued {new Date(cert.issued_at).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" })}</>}
        </p>

        <a
          href={certificatePdfUrl(token)}
          data-testid="certificate-download-btn"
          className="inline-flex items-center gap-2 bg-[#F57C00] hover:bg-[#E67300] text-white font-bold px-8 py-4 rounded-md transition-colors duration-300"
        >
          <FileDown size={18} /> Download certificate PDF
        </a>
      </motion.div>
    </div>
  );
}
